import { useTranslation } from 'react-i18next';

import type { LeadStage, SlaStatus, Temperature } from '@/types/app';

// Colour carries meaning here, so it is never the only signal — each badge also
// shows its label as text.
const STAGE_TONE: Record<LeadStage, string> = {
  new: 'bg-sky-50 text-sky-800 border-sky-200',
  contacted: 'bg-sky-50 text-sky-800 border-sky-200',
  qualified: 'bg-indigo-50 text-indigo-800 border-indigo-200',
  interested: 'bg-indigo-50 text-indigo-800 border-indigo-200',
  follow_up: 'bg-amber-50 text-amber-800 border-amber-200',
  meeting: 'bg-violet-50 text-violet-800 border-violet-200',
  site_visit: 'bg-violet-50 text-violet-800 border-violet-200',
  offer: 'bg-teal-50 text-teal-800 border-teal-200',
  negotiation: 'bg-teal-50 text-teal-800 border-teal-200',
  reservation: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  contract: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  won: 'bg-green-100 text-green-900 border-green-300',
  lost: 'bg-slate-100 text-slate-600 border-slate-300',
};

export function StageBadge({ stage }: { stage: LeadStage }) {
  const { t } = useTranslation();
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium ${STAGE_TONE[stage]}`}
    >
      {t(`stages.${stage}` as 'stages.new')}
    </span>
  );
}

const TEMPERATURE_TONE: Record<Temperature, string> = {
  hot: 'bg-red-50 text-red-700 border-red-200',
  warm: 'bg-amber-50 text-amber-700 border-amber-200',
  cold: 'bg-slate-50 text-slate-600 border-slate-200',
};

export function TemperatureBadge({ temperature }: { temperature: Temperature | null }) {
  const { t } = useTranslation();
  if (!temperature) return <span className="text-slate-400">—</span>;
  return (
    <span
      className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${TEMPERATURE_TONE[temperature]}`}
    >
      {t(`temperatures.${temperature}` as 'temperatures.hot')}
    </span>
  );
}

const SLA_TONE: Record<SlaStatus, string> = {
  pending: 'bg-amber-50 text-amber-800 border-amber-200',
  met: 'bg-green-50 text-green-800 border-green-200',
  breached: 'bg-red-50 text-red-800 border-red-200',
};

export function SlaBadge({ status }: { status: SlaStatus }) {
  const { t } = useTranslation();
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium ${SLA_TONE[status]}`}
    >
      {t(`slaStatuses.${status}` as 'slaStatuses.pending')}
    </span>
  );
}
